import { convertDistance, getDistance } from "geolib"
import { graphql, useStaticQuery, navigate } from "gatsby"
import _ from "lodash"
import PropTypes from "prop-types"
import React, {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { PinIcon } from "components/blocks/clinicInfo/infoIcons"
import LocationListCard from "components/blocks/locationListCard"
import PlacesAutocomplete from "components/elements/placesAutocomplete"
import RichText from "components/elements/richText"
import { useStaticQueryGetClinicsAddresses } from "services/graphQl/queries/clinicsAddresses"
import { googleMapURL } from "utils/constants"
import MapErrorBoundary from "./mapErrorBoundary"
import StateIcon from "./stateIcons"
import "./locations.scss"

const Map = lazy(() => import("components/elements/map"))

// How long to wait on the browser location prompt before giving up and showing
// the whole-country view. Without this the page can sit in a loading state
// forever if the visitor ignores the prompt rather than answering it.
const GEOLOCATION_TIMEOUT = 8000

// How far the map zooms in on a visitor or a searched place. 9 shows a metro
// area, so the nearby centers are visible around the point.
const LOCAL_ZOOM = 9

// The Google Maps script loads after the page does, so a fresh visit to a link
// like /locations?address=92008 can arrive before it. Wait for it for a short
// while, rather than quietly ignoring the address.
const MAPS_WAIT = 10000

// Pins and their numbered circles in the brand's Dark Pink ($darkPink). The
// pin is a small inline SVG rather than an image file, so its color lives here
// with the rest of the map's look. Google anchors the bottom center of the
// image, the pin's tip, on the center's location.
const PIN_COLOR = "#9a5e5e"
const PIN_SVG = [
  `<svg xmlns="http://www.w3.org/2000/svg" width="30" height="40" viewBox="0 0 30 40">`,
  `<path d="M15 1C7.3 1 1 7.3 1 15c0 10.6 14 24 14 24s14-13.4 14-24C29 7.3 22.7 1 15 1z"`,
  ` fill="${PIN_COLOR}" stroke="#ffffff" stroke-width="1.5"/>`,
  `<circle cx="15" cy="15" r="5.5" fill="#ffffff"/>`,
  `</svg>`,
].join("")
const MAP_PIN = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
  PIN_SVG
)}`

// Areas Cortica serves without a center of their own keep their card in the
// list but get no pin. Inland Empire families get in-home ABA and drive to the
// Carlsbad center for medical care, so that page sits at Carlsbad's address,
// and two pins on one spot make a "2" circle that never splits, leaving
// neither pin clickable. Listed by the page's web address, which differs
// between the dev and the live content.
const NO_MAP_PIN = new Set([
  "ca/inland-empire", // dev content
  "inland-empire-ca-autism-therapy", // live content
])

// Calmer map colors to match the design: warm land, soft blue water, quiet
// roads and labels, and no business or transit icons competing with our pins.
// Only the zoom buttons are kept from Google's controls.
const MAP_OPTIONS = {
  cameraControl: false,
  fullscreenControl: false,
  mapTypeControl: false,
  streetViewControl: false,
  zoomControl: true,
  styles: [
    { elementType: "geometry", stylers: [{ color: "#f5f2ea" }] },
    { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
    { elementType: "labels.text.fill", stylers: [{ color: "#7d7d7d" }] },
    { elementType: "labels.text.stroke", stylers: [{ color: "#f5f2ea" }] },
    {
      featureType: "administrative",
      elementType: "geometry.stroke",
      stylers: [{ color: "#c9c6bd" }],
    },
    { featureType: "poi", stylers: [{ visibility: "off" }] },
    {
      featureType: "road",
      elementType: "geometry",
      stylers: [{ color: "#ffffff" }],
    },
    {
      featureType: "road.highway",
      elementType: "geometry",
      stylers: [{ color: "#e6e2d9" }],
    },
    { featureType: "transit", stylers: [{ visibility: "off" }] },
    {
      featureType: "water",
      elementType: "geometry",
      stylers: [{ color: "#b7d5ec" }],
    },
    {
      featureType: "water",
      elementType: "labels.text.fill",
      stylers: [{ color: "#86a9c2" }],
    },
  ],
}

const whenMapsReady = (callback, isCancelled) => {
  const startedAt = Date.now()
  const check = () => {
    if (isCancelled()) return
    if (window.google?.maps?.Geocoder) {
      callback()
      return
    }
    if (Date.now() - startedAt < MAPS_WAIT) setTimeout(check, 200)
  }
  check()
}

const GetClinicSlugPrefix = () => {
  const query = useStaticQuery(
    graphql`
      query ClinicSlugPrefix {
        template: contentfulWebPageTemplate(model: { eq: "Clinic" }) {
          slugPrefix
        }
      }
    `
  )
  return query.template.slugPrefix
}

// The id a state's section is jumped to by, such as "locations-north-carolina"
const stateSectionId = state =>
  `locations-${state.toLowerCase().replace(/[^a-z]+/g, "-")}`

// The outer edges of each state, so a state pill can move the map to frame
// the whole state. From the US Census boundary data the state icons were
// drawn from.
const STATE_AREAS = {
  Arizona: { east: -109.04, north: 37, south: 31.33, west: -114.81 },
  California: { east: -114.13, north: 42.01, south: 32.53, west: -124.41 },
  Connecticut: { east: -71.79, north: 42.05, south: 40.98, west: -73.73 },
  Illinois: { east: -87.49, north: 42.51, south: 36.97, west: -91.51 },
  Massachusetts: { east: -69.93, north: 42.89, south: 41.24, west: -73.51 },
  "New Jersey": { east: -73.89, north: 41.36, south: 38.93, west: -75.56 },
  "North Carolina": { east: -75.46, north: 36.59, south: 33.85, west: -84.32 },
  Texas: { east: -93.52, north: 36.5, south: 25.84, west: -106.65 },
}

// A state not listed above, such as one Cortica opens in later, is framed
// around its centers instead, with at least a degree of room on each side so
// a single center does not end up as an extreme close-up.
const areaForState = (state, stateClinics) => {
  if (STATE_AREAS[state]) return STATE_AREAS[state]

  const lats = stateClinics.map(clinic => clinic.address.lat)
  const lons = stateClinics.map(clinic => clinic.address.lon)
  return {
    east: Math.max(...lons) + 1,
    north: Math.max(...lats) + 1,
    south: Math.min(...lats) - 1,
    west: Math.min(...lons) - 1,
  }
}

const milesBetween = (origin, clinic) =>
  Math.round(
    convertDistance(
      getDistance(
        { latitude: origin.lat, longitude: origin.lng },
        { latitude: clinic.address.lat, longitude: clinic.address.lon }
      ),
      "mi"
    )
  )

const MapLoading = () => (
  <div className="Locations__mapLoading">
    <div className="Locations__spinner" />
    <p>Loading map...</p>
  </div>
)

const MapUnavailable = () => (
  <div className="Locations__mapLoading" role="status">
    <p>
      {"The map couldn't load. You can still find every center in the list."}
    </p>
  </div>
)

const LocationsPage = ({ additionalText, isFirstSection, title }) => {
  const slugPrefix = GetClinicSlugPrefix()
  const clinics = useStaticQueryGetClinicsAddresses()
  const allClinics = useMemo(() => _.sortBy(clinics, ["name"]), [clinics])

  // Two possible places to measure distances from. geoOrigin comes from the
  // browser location prompt, searchOrigin from the search box. A search wins
  // while it is active, and clearing it falls back to where the visitor is.
  // With neither, the list is grouped by state, which is also where a denied
  // prompt lands.
  const [geoOrigin, setGeoOrigin] = useState(null)
  const [searchOrigin, setSearchOrigin] = useState(null)
  const origin = searchOrigin || geoOrigin

  // The search field manages its own text. The page keeps the latest text in
  // a ref, so typing does not redraw every card and the map, and only tracks
  // whether there is any text, for the clear button.
  const searchTextRef = useRef("")
  const [hasSearchText, setHasSearchText] = useState(false)
  const [searchError, setSearchError] = useState("")
  // Changing this redraws the field with new starting text: empty after a
  // clear, or the address from an ?address= link.
  const [fieldSeed, setFieldSeed] = useState({ key: 0, text: "" })

  const [highlightedSlug, setHighlightedSlug] = useState(null)
  const [mapCenter, setMapCenter] = useState(null)
  const [mapZoom, setMapZoom] = useState(null)

  const cardRefs = useRef({})
  const isMounted = useRef(false)
  const searchOriginRef = useRef(null)

  // Lets late answers (the location prompt, a search lookup) check the page is
  // still open before updating it. Set on every start, not just the first, as
  // React can stop and restart a component.
  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
    }
  }, [])

  useEffect(() => {
    searchOriginRef.current = searchOrigin
  }, [searchOrigin])

  // The map loads lazily, to keep it out of the page's first download. React
  // 16 cannot render lazy-loaded parts on a server, and Gatsby's build renders
  // every page to HTML on a server first, so the build fails if the map is in
  // that first render. The loading box stands in until the page is running in
  // the browser.
  const [isInBrowser, setIsInBrowser] = useState(false)

  useEffect(() => {
    setIsInBrowser(true)
  }, [])

  // The map sits flush under the site header. The header shows its
  // announcement bar and nav bar while the visitor scrolls up, and tucks the
  // announcement bar away while they scroll down. Its height also depends on
  // the announcement text, which comes from Contentful and can wrap onto two
  // lines or be missing. So the map follows it rather than using a fixed gap.
  //
  // The full height is kept too, for the state pills' jumps: a jump upward
  // brings the whole header back, so a section must land below all of it.
  const [headerHeights, setHeaderHeights] = useState(null)

  useEffect(() => {
    const header = document.querySelector("header.g-header")
    const nav = header && header.querySelector(".g-header-top-nav")
    if (!nav) return undefined

    const update = () => {
      // The open mobile menu stretches the header to the full screen.
      if (header.classList.contains("--open")) return
      // The announcement bar sits in the header itself and the nav bar is
      // pinned below it. When the header tucks away, only the nav bar shows.
      const full = header.offsetHeight + nav.offsetHeight
      const tucked = header.classList.contains("--hidden")
      setHeaderHeights({ full, visible: tucked ? nav.offsetHeight : full })
    }

    update()
    const observer = new MutationObserver(update)
    observer.observe(header, { attributeFilter: ["class"] })
    window.addEventListener("resize", update)
    return () => {
      observer.disconnect()
      window.removeEventListener("resize", update)
    }
  }, [])

  const clinicsWithSlugs = useMemo(
    () =>
      allClinics.map(clinic => ({
        ...clinic,
        fullSlug: `/${slugPrefix}/${clinic.slug}`,
      })),
    [allClinics, slugPrefix]
  )

  // With an origin the list becomes one flat run of centers, nearest first.
  // Without one it stays grouped under state headings.
  const sortedClinics = useMemo(() => {
    if (!origin) return clinicsWithSlugs

    return clinicsWithSlugs
      .map(clinic => ({ ...clinic, distance: milesBetween(origin, clinic) }))
      .sort((a, b) => a.distance - b.distance)
  }, [clinicsWithSlugs, origin])

  // Grouped by state for the state view and the state pills. When we know
  // where the visitor is, the cards keep their distances here too.
  const groupedByState = useMemo(() => {
    const clinicsToGroup = origin
      ? clinicsWithSlugs.map(clinic => ({
          ...clinic,
          distance: milesBetween(origin, clinic),
        }))
      : clinicsWithSlugs
    const groups = _.groupBy(clinicsToGroup, "state")
    return Object.keys(groups)
      .sort()
      .map(state => ({ clinics: groups[state], state }))
  }, [clinicsWithSlugs, origin])

  // The state pills jump to a state's section. A visitor whose list is sorted
  // nearest first is switched to the state view so that section exists, and
  // can switch back. A new search or clearing the search switches back too.
  const [showStates, setShowStates] = useState(false)
  const [jumpTarget, setJumpTarget] = useState(null)
  // The area the map should move to after a state pill, a fresh object each
  // time so picking the same state again frames it again.
  const [mapFrame, setMapFrame] = useState(null)
  const listByState = !origin || showStates

  const jumpToState = useCallback((state, stateClinics) => {
    setShowStates(true)
    // A fresh object each time, so clicking the same pill again still jumps.
    setJumpTarget({ state })
    setMapFrame({ ...areaForState(state, stateClinics) })
  }, [])

  // Scrolls once the state view is on screen. The sections keep clear of the
  // site header through scroll-margin-top in the stylesheet.
  useEffect(() => {
    if (!jumpTarget) return
    const section = document.getElementById(stateSectionId(jumpTarget.state))
    if (section) section.scrollIntoView({ behavior: "smooth", block: "start" })
  }, [jumpTarget])

  // A point zooms the map in on it. Null frames every center in the country.
  const focusMapOn = useCallback(point => {
    setMapCenter(point ? { lat: point.lat, lng: point.lng } : null)
    setMapZoom(point ? LOCAL_ZOOM : null)
  }, [])

  // Back to nearest first, with the map back on the visitor's area too.
  const showNearestFirst = useCallback(() => {
    setShowStates(false)
    focusMapOn(origin)
  }, [focusMapOn, origin])

  const updateSearchText = useCallback(text => {
    searchTextRef.current = text
    setHasSearchText(Boolean(text))
    setSearchError("")
  }, [])

  const showSearchResult = useCallback(
    geoCoor => {
      setSearchOrigin(geoCoor)
      setSearchError("")
      setHighlightedSlug(null)
      setShowStates(false)
      focusMapOn(geoCoor)
    },
    [focusMapOn]
  )

  const clearSearch = useCallback(() => {
    searchTextRef.current = ""
    setHasSearchText(false)
    setSearchError("")
    setFieldSeed(seed => ({ key: seed.key + 1, text: "" }))
    setSearchOrigin(null)
    setHighlightedSlug(null)
    setShowStates(false)
    focusMapOn(geoOrigin)
  }, [focusMapOn, geoOrigin])

  const handlePlaceChanged = useCallback(
    geoCoor => {
      if (!geoCoor) {
        clearSearch()
        return
      }
      showSearchResult(geoCoor)
    },
    [clearSearch, showSearchResult]
  )

  // Looks up typed text: for the Search button, for Enter pressed without
  // choosing a suggestion, and for the ?address= links the homepage search
  // sends here.
  const geocodeText = useCallback(
    text => {
      const query = (text || "").trim()
      if (!query) return

      whenMapsReady(
        () => {
          new window.google.maps.Geocoder().geocode(
            { address: query, componentRestrictions: { country: "US" } },
            (results, status) => {
              if (!isMounted.current) return

              if (status === "OK" && results[0]) {
                const { location } = results[0].geometry
                showSearchResult({ lat: location.lat(), lng: location.lng() })
              } else if (status === "ZERO_RESULTS") {
                setSearchError(
                  "We couldn't find that place. Try an address, city, or zip code."
                )
              } else {
                // Google refused or failed the lookup. Say so, rather than
                // leaving the button looking like it does nothing.
                setSearchError(
                  "Search isn't working right now. Please try again in a moment."
                )
              }
            }
          )
        },
        () => !isMounted.current
      )
    },
    [showSearchResult]
  )

  // On arrival: honor an ?address= link if there is one, otherwise ask the
  // browser where the visitor is. A denial or a timeout leaves the page on the
  // whole-country view, which is also what everyone sees before answering.
  useEffect(() => {
    const addressParam = new URLSearchParams(window.location.search).get(
      "address"
    )

    if (addressParam) {
      updateSearchText(addressParam)
      setFieldSeed(seed => ({ key: seed.key + 1, text: addressParam }))
      geocodeText(addressParam)
      return
    }

    if (!navigator.geolocation) return

    navigator.geolocation.getCurrentPosition(
      position => {
        if (!isMounted.current) return

        const coords = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        }
        setGeoOrigin(coords)
        // A search made while the prompt was still open takes priority.
        if (!searchOriginRef.current) focusMapOn(coords)
      },
      () => {},
      { maximumAge: 300000, timeout: GEOLOCATION_TIMEOUT }
    )
    // Runs once, on arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // A tapped pin is looked up by its place in this list, so pins are left out
  // here rather than inside the map.
  const markerPositions = useMemo(
    () =>
      clinicsWithSlugs
        .filter(clinic => !NO_MAP_PIN.has(clinic.slug))
        .map(clinic => ({
          ...clinic.address,
          detailedAddress: clinic.detailedAddress,
          fullSlug: clinic.fullSlug,
          info: clinic.name,
          name: clinic.name,
          showInfo: false,
        })),
    [clinicsWithSlugs]
  )

  // Tapping a pin scrolls its card into view and marks it, so the map and the
  // list never feel like two separate things.
  const handleMarkerClick = useCallback(
    index => {
      const marker = markerPositions[index]
      if (!marker) return

      setHighlightedSlug(marker.fullSlug)

      const node = cardRefs.current[marker.fullSlug]
      if (node) node.scrollIntoView({ behavior: "smooth", block: "center" })
    },
    [markerPositions]
  )

  const renderInfoContent = useCallback(
    marker => (
      <div className="Locations__pinCard">
        <p className="Locations__pinName">{marker.name}</p>
        <p className="Locations__pinAddress">{marker.detailedAddress}</p>
        <button
          className="Locations__pinLink"
          onClick={() => navigate(marker.fullSlug)}
          type="button"
        >
          View Location
        </button>
      </div>
    ),
    []
  )

  const renderCard = clinic => (
    <LocationListCard
      {...clinic}
      innerRef={node => {
        cardRefs.current[clinic.fullSlug] = node
      }}
      isHighlighted={highlightedSlug === clinic.fullSlug}
      key={clinic.id}
      slug={clinic.fullSlug}
    />
  )

  return (
    <div
      className="Locations"
      style={
        headerHeights
          ? {
              "--locations-header-full": `${headerHeights.full}px`,
              "--locations-sticky-top": `${headerHeights.visible}px`,
            }
          : undefined
      }
    >
      <div className="Locations__inner" id="content">
        <div className="Locations__layout">
          <div className="Locations__top">
            {/* The heading and intro (this section's Title and Additional
                Text in Contentful) appear only when the locator is the first
                section on the page, which is what removing the old page header
                above it does. Until then that header stays the only heading,
                so this code can go live before the content changes, and the
                page never has two main headings or none. */}
            {isFirstSection && (title || additionalText) && (
              <div className="Locations__intro">
                {title && <h1 className="Locations__title">{title}</h1>}
                {additionalText && (
                  <div className="Locations__lede">
                    <RichText richText={additionalText} textStyle="full" />
                  </div>
                )}
              </div>
            )}

            {/* Every state Cortica serves, with its number of centers, so
                visitors see the whole spread before the long list. Built from
                the center list, so a new state or center appears on its own. */}
            <nav aria-label="Jump to a state" className="Locations__states">
              {groupedByState.map(({ clinics: stateClinics, state }) => {
                const count = stateClinics.length
                return (
                  <button
                    aria-label={`${state}, ${count} ${
                      count === 1 ? "center" : "centers"
                    }`}
                    className="Locations__statePill"
                    key={state}
                    onClick={() => jumpToState(state, stateClinics)}
                    type="button"
                  >
                    <StateIcon
                      className="Locations__statePillIcon"
                      state={state}
                    />
                    {state}
                    <span className="Locations__statePillCount">{count}</span>
                  </button>
                )
              })}
            </nav>

            <div className="Locations__search">
              <p className="Locations__searchHint">
                <PinIcon />
                Search by address, city, or zip code
              </p>

              <PlacesAutocomplete
                defaultValue={fieldSeed.text}
                googleMapURL={googleMapURL}
                hasValue={hasSearchText}
                key={fieldSeed.key}
                loadingElement={<div style={{ height: "100%" }} />}
                onClear={clearSearch}
                onTextChange={updateSearchText}
                onUnmatchedText={geocodeText}
                placeChanged={handlePlaceChanged}
                placeholder="Ex. San Diego"
                showSearchIcon
              />

              {searchError && (
                <p className="Locations__searchError" role="alert">
                  {searchError}
                </p>
              )}

              <button
                className="Locations__searchButton"
                onClick={() => geocodeText(searchTextRef.current)}
                type="button"
              >
                Search
                <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
                  <path
                    d="M5 12h13m0 0-5-5m5 5-5 5"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                  />
                </svg>
              </button>
            </div>
          </div>

          <div className="Locations__mapColumn">
            <div className="Locations__map">
              <MapErrorBoundary fallback={<MapUnavailable />}>
                {isInBrowser ? (
                  <Suspense fallback={<MapLoading />}>
                    <Map
                      clusterColor={PIN_COLOR}
                      containerElement={
                        <div className="Locations__mapCanvas" />
                      }
                      enableClustering
                      // Fit the pins exactly, so the whole country fills the
                      // map as in the design instead of sitting small in it
                      fitPadding={0}
                      frameTarget={mapFrame}
                      googleMapURL={googleMapURL}
                      handleMarkerClick={handleMarkerClick}
                      infoMode="click"
                      isMarkerShown
                      loadingElement={<MapLoading />}
                      mapCenter={mapCenter}
                      mapElement={<div style={{ height: "100%" }} />}
                      mapOptions={MAP_OPTIONS}
                      mapZoom={mapZoom}
                      markerIcon={MAP_PIN}
                      markerPosition={markerPositions}
                      renderInfoContent={renderInfoContent}
                      zoomChange={setMapZoom}
                    />
                  </Suspense>
                ) : (
                  <MapLoading />
                )}
              </MapErrorBoundary>
            </div>
          </div>

          <div className="Locations__results">
            {origin && showStates && (
              <button
                className="Locations__nearestToggle"
                onClick={showNearestFirst}
                type="button"
              >
                <PinIcon />
                Show nearest first
              </button>
            )}

            {!listByState
              ? sortedClinics.map(renderCard)
              : groupedByState.map(({ clinics: stateClinics, state }) => (
                  <section
                    className="Locations__group"
                    id={stateSectionId(state)}
                    key={state}
                  >
                    <h2 className="Locations__groupTitle">
                      <StateIcon
                        className="Locations__groupIcon"
                        state={state}
                      />
                      {state}
                    </h2>
                    <div className="Locations__groupCards">
                      {stateClinics.map(renderCard)}
                    </div>
                  </section>
                ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export const LocationsPageProps = {
  additionalText: PropTypes.shape({
    json: PropTypes.object,
  }),
  isFirstSection: PropTypes.bool,
  title: PropTypes.string,
}

LocationsPage.propTypes = LocationsPageProps

export default LocationsPage
